import { useMemo, useState } from 'react';
import { formatDate, formatEuro, formatEuroExact, formatSquareMeters } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Pill, Skeleton, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import {
  ReleaseReservationDialog,
  ReservationStatusCard,
  ReserveUnitDialog,
  unitStatusVariants,
} from '../../reservations/index';
import { useDealReservations, useDealUnits, useInvalidateDeals } from '../api';
import type { DealRecord, DealUnit } from '../types';
import { NextStepCard, ParticipantsCard, PropertyCard } from './bits';
import { AddUnitDialog, UnitChangeDialog } from './UnitDialogs';
import styles from './record.module.css';

/**
 * Deal-Akte 04 „Einheiten": the unit card(s) with the facts, „Einheit wechseln" / „Einheit hinzufügen",
 * the reservation of the unit (the reservations module's dialogs and status card) and the change history.
 */

type DialogState =
  | { kind: 'change'; unit: DealUnit }
  | { kind: 'add' }
  | { kind: 'reserve'; unit: DealUnit }
  | { kind: 'release'; reservationId: string };

function fact(label: string, value: string, sub?: string | null) {
  return (
    <div className={styles.fact} key={label}>
      <div className={styles.eyebrow}>{label}</div>
      <div className={styles.factValue}>{value}</div>
      {sub ? <div className={styles.factSub}>{sub}</div> : null}
    </div>
  );
}

/** Secondary line under the actual rent: €/m² of the actual rent, then target rent and rent subsidy when set. */
function rentLine(unit: DealUnit): string | null {
  const parts: string[] = [];
  if (unit.rentActualCents !== null && unit.areaSqm) {
    parts.push(`${formatEuroExact(Math.round(unit.rentActualCents / unit.areaSqm))}/m²`);
  }
  if (unit.rentTargetCents !== null) parts.push(`Soll-Miete ${formatEuro(unit.rentTargetCents)}`);
  if (unit.rentSubsidyCents !== null) {
    parts.push(`Mietsubvention ${formatEuro(unit.rentSubsidyCents)} einmalig`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function coOwnership(share: number | null): string {
  if (share === null) return '–';
  return `${share.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / 1.000`;
}

export function UnitsTab({ deal }: { deal: DealRecord }) {
  const { toast } = useToast();
  const invalidate = useInvalidateDeals();
  const units = useDealUnits(deal.id, true);
  const reservations = useDealReservations(deal.id, true);
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const active = useMemo(() => units.data?.units.filter((unit) => unit.active) ?? [], [units.data]);
  const live = useMemo(
    () =>
      (reservations.data?.items ?? []).filter(
        (item) =>
          item.status === 'awaiting_signature' ||
          item.status === 'awaiting_approval' ||
          item.status === 'active',
      ),
    [reservations.data],
  );
  const canWrite = deal.permissions.canChangeUnit && !deal.permissions.readOnly;

  function done(message: string | null) {
    setDialog(null);
    void invalidate(deal.id);
    if (message !== null) toast({ message });
  }

  return (
    <div className={styles.content}>
      <div className={styles.main}>
        {units.isPending ? (
          <section className={styles.card}>
            <div className={styles.skeletonStack}>
              <Skeleton variant="block" height={120} />
              <Skeleton width="60%" />
              <Skeleton width="40%" />
            </div>
          </section>
        ) : null}
        {units.isError ? <Banner tone="danger">{errorMessage(units.error)}</Banner> : null}
        {units.data && active.length === 0 ? (
          <section className={styles.card} aria-label="Einheit">
            <div className={styles.emptyBlock}>
              <div className={styles.emptyTitle}>Noch keine Einheit</div>
              <p className={styles.emptyText}>
                Der Deal läuft ohne Einheit. Für „Reserviert“ muss eine Einheit gewählt sein; mit der Wahl
                geht der Deal auf „Einheit gewählt“.
              </p>
              {canWrite ? (
                <div style={{ marginTop: 14 }}>
                  <Button onClick={() => setDialog({ kind: 'add' })}>Einheit wählen</Button>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}
        {active.map((unit) => {
          const reservation = live.find((item) => item.unit.id === unit.id) ?? null;
          const canReserve =
            canWrite && !reservation && unit.status === 'available' && deal.status === 'active';
          return (
            <section key={unit.id} className={styles.card} aria-label={`Einheit ${unit.number}`}>
              <div className={styles.unitCard}>
                <div className={styles.unitImage}>Grundriss {unit.number}</div>
                <div>
                  <div className={styles.unitHead}>
                    <span className={styles.unitNumber}>{unit.number}</span>
                    <Pill variant={unitStatusVariants[unit.status]}>{unit.statusLabel}</Pill>
                    {canWrite ? (
                      <span className={styles.unitActions}>
                        {canReserve ? (
                          <Button size="small" onClick={() => setDialog({ kind: 'reserve', unit })}>
                            Einheit reservieren
                          </Button>
                        ) : null}
                        {reservation && (reservation.canRelease || reservation.canRequestRelease) ? (
                          <Button
                            size="small"
                            variant="secondary"
                            onClick={() => setDialog({ kind: 'release', reservationId: reservation.id })}
                          >
                            Reservierung aufheben
                          </Button>
                        ) : null}
                        {deal.status === 'active' ? (
                          <>
                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() => setDialog({ kind: 'change', unit })}
                            >
                              Einheit wechseln
                            </Button>
                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() => setDialog({ kind: 'add' })}
                            >
                              Einheit hinzufügen
                            </Button>
                          </>
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.unitPlace}>
                    {[unit.propertyName, unit.building, unit.floor].filter(Boolean).join(' · ')}
                  </div>
                  <div className={styles.facts}>
                    {fact('Wohnfläche', unit.areaSqm === null ? '–' : formatSquareMeters(unit.areaSqm))}
                    {fact('Zimmer', unit.rooms === null ? '–' : String(unit.rooms))}
                    {fact('Kaufpreis', unit.priceCents === null ? '–' : formatEuro(unit.priceCents))}
                    {fact(
                      'Miete ist',
                      unit.rentActualCents === null ? '–' : formatEuro(unit.rentActualCents),
                      rentLine(unit),
                    )}
                    {fact(
                      'Hausgeld',
                      unit.serviceChargeCents === null ? '–' : formatEuro(unit.serviceChargeCents),
                    )}
                    {fact('MEA', coOwnership(unit.coOwnershipShare))}
                    {fact('Stellplatz', unit.parkingSpaceNumber ?? '–')}
                    {fact('Reserviert seit', unit.reservedSince ? formatDate(unit.reservedSince) : '–')}
                  </div>
                </div>
              </div>
              {reservation ? (
                <div style={{ marginTop: 18 }}>
                  <ReservationStatusCard
                    reservationId={reservation.id}
                    onChanged={() => void invalidate(deal.id)}
                  />
                </div>
              ) : null}
            </section>
          );
        })}
        {units.data ? (
          <section className={styles.card} aria-label="Wechsel-Historie">
            <div className={styles.cardTitle}>Wechsel-Historie</div>
            <p className={styles.cardIntro}>
              Einheiten werden bei Beratung oder Finanzierung gewechselt. Die alte Einheit wird automatisch
              frei.
            </p>
            <div style={{ marginTop: 14 }}>
              {units.data.history.length === 0 ? (
                <div className={styles.grid} style={{ gridTemplateColumns: 'minmax(0,1fr)' }}>
                  <span className={styles.gridMuted}>Noch kein Wechsel.</span>
                </div>
              ) : null}
              {units.data.history.map((row) => (
                <div
                  key={row.id}
                  className={styles.grid}
                  style={{ gridTemplateColumns: '110px 180px minmax(0,1fr) 110px' }}
                >
                  <span className={styles.gridMuted}>{formatDate(row.occurredOn)}</span>
                  <span className={clsx(styles.gridCell, styles.gridStrong)}>
                    {row.fromUnit} → {row.toUnit}
                  </span>
                  <span className={clsx(styles.gridCell, styles.gridMuted)}>
                    {row.reasonLabel}
                    {row.note ? ` · ${row.note}` : ''}
                    {row.financierInformed ? ' · Finanzierer informiert' : ''}
                  </span>
                  <span className={clsx(styles.gridCell, styles.gridMuted, styles.gridRight)}>
                    {row.actorShort ?? '–'}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
      <div className={styles.side}>
        <NextStepCard deal={deal} />
        <ParticipantsCard deal={deal} />
        <PropertyCard deal={deal} />
      </div>

      <UnitChangeDialog
        open={dialog?.kind === 'change'}
        dealId={deal.id}
        fromUnit={dialog?.kind === 'change' ? { id: dialog.unit.id, number: dialog.unit.number } : null}
        alternatives={units.data?.alternatives ?? []}
        onClose={() => setDialog(null)}
        onDone={(updated) => done(`Einheit gewechselt: ${updated.unitLine}.`)}
      />
      <AddUnitDialog
        open={dialog?.kind === 'add'}
        dealId={deal.id}
        alternatives={units.data?.alternatives ?? []}
        contactId={deal.contact.id}
        first={active.length === 0}
        onClose={() => setDialog(null)}
        onDone={(updated) => done(`Einheit gewählt: ${updated.unitLine}.`)}
      />
      {dialog?.kind === 'reserve' ? (
        <ReserveUnitDialog
          open
          unitId={dialog.unit.id}
          contactId={deal.contact.id}
          onClose={() => setDialog(null)}
          onReserved={() => done('Reservierung gestartet.')}
        />
      ) : null}
      {dialog?.kind === 'release' ? (
        <ReleaseReservationDialog
          open
          reservationId={dialog.reservationId}
          onClose={() => setDialog(null)}
          // the release dialog shows its own undo toast (Reservierung 13); only the request gets a note here
          onReleased={(item) => done(item ? null : 'Aufhebung beantragt.')}
        />
      ) : null}
    </div>
  );
}
